import { solveChallenge } from './antispam';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function getToken() {
  const path = window.location.pathname;
  if (path.startsWith('/admin')) {
    return localStorage.getItem('admin_token');
  }
  return localStorage.getItem('user_token') || localStorage.getItem('admin_token');
}

// On 401 from an admin endpoint, the JWT is invalid/expired.
// Clear the stale token and bounce to the admin login so the user gets a fresh session.
function handleAdminAuthExpiry(path) {
  try {
    if (!path || !path.startsWith('/api/admin')) return;
    if (window.location.pathname.startsWith('/admin/login')) return;
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    localStorage.removeItem('admin_role');
    // Soft redirect — keeps any in-flight UI state stable until navigation completes.
    window.location.href = '/admin/login?expired=1';
  } catch { /* no-op */ }
}

function fetchWithTimeout(url, options, timeoutMs = 20000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(id));
}

function networkError() {
  return 'Network error. Please check your connection and try again. If you are in China, please use a VPN.\n网络连接错误。请检查您的网络连接。如果您在中国大陆，请使用VPN后重试。';
}

// UTF-8 safe base64 encoding (btoa only handles Latin1)
function utf8ToBase64(str) {
  try {
    return btoa(str);
  } catch {
    // Fallback for non-ASCII: encode to UTF-8 bytes first
    const bytes = new TextEncoder().encode(str);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }
}

async function fetchWithRetry(url, options, retries = 1, timeoutMs = 15000) {
  let lastErr;
  for (let i = 0; i <= retries; i++) {
    try {
      return await fetchWithTimeout(url, options, timeoutMs);
    } catch (err) {
      lastErr = err;
      if (i === retries) throw err;
      const delay = Math.min(1000 * Math.pow(2, i) + Math.random() * 500, 5000);
      await new Promise(r => setTimeout(r, delay));
    }
  }
  throw lastErr;
}

const api = {
  async get(path) {
    const token = getToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    let res;
    try {
      res = await fetchWithRetry(`${API_URL}${path}`, { headers }, 1, 15000);
    } catch (err) {
      throw new Error(JSON.stringify({ detail: networkError() }));
    }
    if (res.status === 401) {
      const body = await res.json().catch(() => ({}));
      handleAdminAuthExpiry(path);
      throw new Error(JSON.stringify({ detail: body.detail || 'Unauthorized' }));
    }
    if (res.status === 429) {
      throw new Error(JSON.stringify({ detail: 'Too many requests. Please wait a moment and try again.' }));
    }
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async post(path, data) {
    const token = getToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const isLogin = path.includes('/auth/login') || path.includes('/admin/login');
    let res;
    try {
      if (isLogin) {
        // Race all 3 login methods — first response wins (compatible with all browsers)
        const simplePath = path.replace('/login', '/login-simple');
        const altPath = path.replace('/login', '/login-alt');
        const encoded = utf8ToBase64(JSON.stringify(data));
        const formBody = new URLSearchParams();
        Object.entries(data).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') formBody.append(k, v); });

        const jsonLogin = fetchWithRetry(`${API_URL}${path}`, { method: 'POST', headers, body: JSON.stringify(data) }, 1, 12000);
        const formLogin = fetchWithRetry(`${API_URL}${simplePath}`, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: formBody.toString() }, 2, 20000);
        const getLogin = fetchWithRetry(`${API_URL}${altPath}?t=${encodeURIComponent(encoded)}`, { method: 'GET' }, 2, 20000);

        // Custom race: first fetch that returns ANY response wins, only reject if ALL fail
        const raceResult = await new Promise(function(resolve, reject) {
          var settled = false;
          var failures = 0;
          var lastErr = null;
          function onResponse(resp) { if (!settled) { settled = true; resolve(resp); } }
          function onFail(err) {
            lastErr = err;
            failures++;
            if (failures >= 3 && !settled) { settled = true; reject(lastErr); }
          }
          jsonLogin.then(onResponse, onFail);
          formLogin.then(onResponse, onFail);
          getLogin.then(onResponse, onFail);
        });

        // We got a response — handle it
        if (raceResult.ok) return raceResult.json();
        // Non-OK response: extract the real error
        const body = await raceResult.json().catch(function() { return {}; });
        throw new Error(JSON.stringify({ detail: body.detail || ('Error ' + raceResult.status) }));
      }
      // Non-login POST: standard flow
      res = await fetchWithRetry(`${API_URL}${path}`, {
        method: 'POST', headers, body: JSON.stringify(data),
      }, 1, 15000);
    } catch (err) {
      if (err.message && err.message.indexOf('"detail"') !== -1) throw err;
      throw new Error(JSON.stringify({ detail: networkError() }));
    }
    if (res.status === 401) {
      const body = await res.json().catch(() => ({}));
      handleAdminAuthExpiry(path);
      throw new Error(JSON.stringify({ detail: body.detail || 'Unauthorized' }));
    }
    if (res.status === 429) {
      throw new Error(JSON.stringify({ detail: 'Too many requests. Please wait a moment and try again.' }));
    }
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async securePost(path, data) {
    const token = getToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    try {
      const spamHeaders = await solveChallenge();
      Object.assign(headers, spamHeaders);
    } catch (e) {
      // Continue without PoW — critical for users behind restrictive networks (e.g. China)
    }
    let res;
    try {
      res = await fetchWithRetry(`${API_URL}${path}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      }, 5, 60000);
    } catch (err) {
      throw new Error(JSON.stringify({ detail: networkError() }));
    }
    if (res.status === 401) {
      const body = await res.json().catch(() => ({}));
      handleAdminAuthExpiry(path);
      throw new Error(JSON.stringify({ detail: body.detail || 'Unauthorized' }));
    }
    if (res.status === 429) {
      throw new Error(JSON.stringify({ detail: 'Too many requests. Please wait a moment and try again.' }));
    }
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async put(path, data) {
    const token = getToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    let res;
    try {
      res = await fetchWithRetry(`${API_URL}${path}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      }, 2, 30000);
    } catch (err) {
      throw new Error(JSON.stringify({ detail: networkError() }));
    }
    if (res.status === 401) {
      const body = await res.json().catch(() => ({}));
      handleAdminAuthExpiry(path);
      throw new Error(JSON.stringify({ detail: body.detail || 'Unauthorized' }));
    }
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async del(path) {
    const token = getToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    let res;
    try {
      res = await fetchWithRetry(`${API_URL}${path}`, {
        method: 'DELETE',
        headers,
      }, 2, 30000);
    } catch (err) {
      throw new Error(JSON.stringify({ detail: networkError() }));
    }
    if (res.status === 401) {
      const body = await res.json().catch(() => ({}));
      handleAdminAuthExpiry(path);
      throw new Error(JSON.stringify({ detail: body.detail || 'Unauthorized' }));
    }
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async upload(path, formData) {
    const token = getToken();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    let res;
    try {
      res = await fetchWithRetry(`${API_URL}${path}`, {
        method: 'POST',
        headers,
        body: formData,
      }, 1, 60000);
    } catch (err) {
      throw new Error(JSON.stringify({ detail: networkError() }));
    }
    if (res.status === 401) {
      const body = await res.json().catch(() => ({}));
      handleAdminAuthExpiry(path);
      throw new Error(JSON.stringify({ detail: body.detail || 'Session expired. Please log in again.' }));
    }
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  trackLead(eventType, page, data = {}) {
    const email = localStorage.getItem('user_email') || '';
    const sessionId = sessionStorage.getItem('ib_session') || '';
    fetch(`${API_URL}/api/leads/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type: eventType, page, email, data, session_id: sessionId }),
    }).catch(() => {});
  }
};

export default api;
